import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../lib/api';
import { Id } from '../convex/_generated/dataModel';

export default function ViewTournament() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const [playerId, setPlayerId] = useState<Id<"players"> | null>(null);
  
  // Get player ID from localStorage
  useEffect(() => {
    const storedPlayerId = localStorage.getItem('playerId');
    if (storedPlayerId) {
      setPlayerId(storedPlayerId as Id<"players">);
    }
  }, []);

  // Fetch tournament data
  const tournament = useQuery(api.tournaments.getTournamentByInvite,
    inviteCode ? { inviteCode } : 'skip'
  );

  const tournamentDetails = useQuery(api.tournaments.getTournamentDetails,
    tournament?._id ? { tournamentId: tournament._id } : 'skip'
  );

  const removeFromTournament = useMutation(api.players.removeFromTournament);
  const registerForTournament = useMutation(api.players.registerForTournament);

  const handleUnregister = async () => {
    if (!playerId || !tournament?._id) return;
    
    if (window.confirm('¿Estás seguro de que quieres cancelar tu registro?')) {
      try {
        await removeFromTournament({ 
          tournamentId: tournament._id, 
          playerId 
        });
        navigate('/dashboard/player');
      } catch (error) {
        console.error('Error unregistering:', error);
      }
    }
  };

  const handleRegister = () => {
    if (!inviteCode) return;
    navigate(`/join/${inviteCode}`);
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/join/${inviteCode}`;
    const text = `¡Únete al torneo ${tournament?.name}! 🎾\n${shareUrl}`;
    
    if (navigator.share) {
      navigator.share({
        title: `Torneo: ${tournament?.name}`,
        text,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Enlace copiado al portapapeles');
    }
  };

  if (!tournament) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Torneo no encontrado</h2>
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const confirmedPlayers = tournamentDetails?.registrations?.filter((r: any) => r.status === 'confirmed') || [];
  const waitlistPlayers = tournamentDetails?.registrations?.filter((r: any) => r.status === 'waitlist') || [];
  const isPlayerRegistered = playerId && confirmedPlayers.some((r: any) => r.playerId === playerId);
  const isPlayerInWaitlist = playerId && waitlistPlayers.some((r: any) => r.playerId === playerId);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="/torneopadel.png" 
              alt="TorneoPadel" 
              className="w-12 h-12 object-contain"
            />
            <h1 className="text-xl font-bold text-gray-900">Ver Torneo</h1>
          </div>
          
          <Link
            to={playerId ? '/dashboard/player' : '/'}
            className="text-gray-600 hover:text-gray-900"
          >
            Volver
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* Tournament Header */}
          <div className="mb-6">
            {tournament.logo && (
              <img 
                src={tournament.logo} 
                alt={tournament.name}
                className="w-32 h-32 object-contain mx-auto mb-4"
              />
            )}
            <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">
              {tournament.name}
            </h1>
            <p className="text-center text-gray-600">Código: {inviteCode}</p>
          </div>

          {/* Actions for registered/non-registered users */}
          {!playerId ? (
            <div className="mb-6 text-center">
              <button
                onClick={handleRegister}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Registrarse al Torneo
              </button>
            </div>
          ) : (isPlayerRegistered || isPlayerInWaitlist) && (
            <div className="mb-6 flex justify-center gap-4">
              <button
                onClick={handleUnregister}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Cancelar Registro
              </button>
              <button
                onClick={handleShare}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Compartir Torneo
              </button>
            </div>
          )}

          {/* Tournament Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <h3 className="font-semibold text-gray-700">Club</h3>
              <p className="text-gray-900">{tournament.club}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700">Fecha</h3>
              <p className="text-gray-900">
                {new Date(tournament.date).toLocaleDateString('es-MX', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700">Hora</h3>
              <p className="text-gray-900">{tournament.time || '18:00'}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700">Formato</h3>
              <p className="text-gray-900 capitalize">{tournament.format}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700">Lugares Disponibles</h3>
              <p className="text-gray-900">
                {tournament.availableSpots} de {tournament.maxPlayers}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700">Estado</h3>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                tournament.status === 'open' 
                  ? 'bg-green-100 text-green-800' 
                  : tournament.status === 'locked'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {tournament.status === 'open' ? 'Abierto' : 
                 tournament.status === 'locked' ? 'Bloqueado' : 'Completado'}
              </span>
            </div>
          </div>

          {/* Description */}
          {tournament.description && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-2">Descripción</h3>
              <p className="text-gray-900 whitespace-pre-wrap">{tournament.description}</p>
            </div>
          )}

          {/* Registered Players */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Jugadores Confirmados ({confirmedPlayers.length})
            </h3>
            {confirmedPlayers.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {confirmedPlayers.map((reg: any, index: number) => (
                  <div key={reg._id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
                    <div className="flex-1">
                      <span className="text-gray-900">{reg.player?.name || 'Jugador'}</span>
                      {reg.partner && (
                        <span className="text-gray-600 ml-2">
                          + {reg.partner.name}
                        </span>
                      )}
                    </div>
                    {playerId === reg.playerId && (
                      <button
                        onClick={handleUnregister}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No hay jugadores registrados aún</p>
            )}
          </div>

          {/* Waitlist */}
          {waitlistPlayers.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Lista de Espera ({waitlistPlayers.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {waitlistPlayers.map((reg: any, index: number) => (
                  <div key={reg._id} className="flex items-center space-x-2 p-2 bg-yellow-50 rounded">
                    <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
                    <div className="flex-1">
                      <span className="text-gray-900">{reg.player?.name || 'Jugador'}</span>
                      {reg.partner && (
                        <span className="text-gray-600 ml-2">
                          + {reg.partner.name}
                        </span>
                      )}
                    </div>
                    {playerId === reg.playerId && (
                      <button
                        onClick={handleUnregister}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}